/**
 * @fileoverview Human-readable audit summary and field-label helpers.
 * @module audit/summaries
 *
 * Responsibilities:
 * - Build end-user summaries for create/update/delete/status/member events.
 * - Map stored field paths to display labels (contactIds → Contacts).
 * - Detect autosave-only patches so mobile can skip noisy audit rows.
 *
 * Dependencies:
 * - ./types (AuditAction)
 *
 * Architecture:
 * - Pure helpers. No I/O. Shared by mobile-pwa writes and AuditLogTimeline.
 *
 * Notes:
 * - Tenant-web services already write human summaries; mobile uses these helpers
 *   so inspector rows match the same language on tenant-web Audit tabs.
 */

import type { AuditAction } from './types';

/**
 * @contract AuditSummaryInput
 * Purpose: Document snapshot used to phrase an audit summary.
 * Fields: collection, action, document, extra (contactName, caseType, fiscalYear).
 * Rules: document may be partial; helpers fall back to a short id.
 * Sync: N/A — input only.
 */
export interface AuditSummaryInput {
  collection: string;
  action: AuditAction;
  document?: Record<string, unknown> | null;
  extra?: {
    contactName?: string;
    entityName?: string;
  };
}

const AUTOSAVE_ONLY_KEYS = new Set([
  'responses',
  'updatedAt',
  'lock',
  'lockId',
  'lockOwner',
  'lockHeartbeatAt',
  '__optimistic',
  '_syncSeq',
]);

const AUTOSAVE_SKIP_COLLECTIONS = new Set(['cases', 'inspectionCases']);

const FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  displayName: 'Name',
  entityName: 'Location',
  entityCategory: 'Category',
  caseStatus: 'Status',
  seriesStatus: 'Series status',
  caseNumber: 'Case number',
  caseType: 'Case type',
  fiscalYear: 'Fiscal year',
  contactIds: 'Contacts',
  caseId: 'Inspection',
  events: 'Events',
  responses: 'Inspection answers',
  parcelNumber: 'Parcel number',
  address: 'Address',
  status: 'Status',
  tags: 'Tags',
  notes: 'Notes',
  email: 'Email',
  phone: 'Phone',
  phoneNumbers: 'Phone numbers',
  emails: 'Emails',
  company: 'Company',
  title: 'Title',
  defaultRoles: 'Roles',
  inspectionAccountId: 'Inspection account',
  inspectionAccountName: 'Inspection account',
  moduleViolationsDraft: 'Violations draft',
  assignedTo: 'Assigned to',
  primaryInspectorId: 'Primary inspector',
};

const COLLECTION_NOUN: Record<string, string> = {
  cases: 'inspection',
  inspectionCases: 'inspection',
  entities: 'location',
  inspectableEntities: 'location',
  contacts: 'contact',
  templates: 'template',
  forms: 'form',
  lookups: 'lookup',
  users: 'user',
};

/**
 * @process isAutosaveOnlyPatch
 *
 * Steps:
 * 1. Return false when the collection is not a case feed.
 * 2. Ignore system keys that always change on write.
 * 3. Return true when remaining keys are only responses / lock heartbeat.
 *
 * Inputs:
 * - collection: Dexie or audit collection name.
 * - patch: fields being written.
 *
 * Outputs:
 * - true when the write must not produce an audit row.
 *
 * Failure Handling: never throws.
 * Idempotency: REQUIRED.
 */
export function isAutosaveOnlyPatch(
  collection: string,
  patch: Record<string, unknown> | null | undefined,
): boolean {
  if (!patch) return false;
  if (!AUTOSAVE_SKIP_COLLECTIONS.has(collection)) return false;
  const keys = Object.keys(patch);
  if (keys.length === 0) return false;
  if (keys.some((key) => !AUTOSAVE_ONLY_KEYS.has(key))) return false;
  return keys.some((key) => key === 'responses' || key.startsWith('lock'));
}

/**
 * @process formatAuditFieldLabel
 *
 * Steps:
 * 1. Look up a known field path (including nested `parent.child`).
 * 2. Fall back to Title Case of the last path segment.
 *
 * Inputs:
 * - field: stored ChangeDetail.field.
 *
 * Outputs:
 * - Display label for timeline / detail table.
 *
 * Failure Handling: never throws.
 * Idempotency: REQUIRED.
 */
/**
 * True when a stored ChangeDetail should be hidden from end-user tables.
 * Used for opaque ids that are already explained by the summary sentence.
 */
export function shouldHideAuditField(field: string): boolean {
  if (!field) return false;
  if (field === 'caseId' || field.endsWith('.caseId')) return true;
  if (field === 'responses') return true;
  const leaf = field.includes('.') ? field.slice(field.lastIndexOf('.') + 1) : field;
  return looksLikeGeneratedFieldId(leaf);
}

function looksLikeGeneratedFieldId(value: string): boolean {
  return /^(fld[_-]|field-\d)/i.test(value) || /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(value);
}

export function formatAuditFieldLabel(field: string): string {
  if (!field) return 'Field';
  if (FIELD_LABELS[field]) return FIELD_LABELS[field];
  if (/[ ?]/.test(field) && !field.includes('.')) return field;
  const leaf = field.includes('.') ? field.slice(field.lastIndexOf('.') + 1) : field;
  if (/[ ?]/.test(leaf)) return leaf;
  if (FIELD_LABELS[leaf]) {
    const parent = field.includes('.') ? field.slice(0, field.lastIndexOf('.')) : '';
    if (!parent) return FIELD_LABELS[leaf];
    if (parent === 'responses') return FIELD_LABELS[leaf];
    return `${titleCase(parent)} › ${FIELD_LABELS[leaf]}`;
  }
  if (looksLikeGeneratedFieldId(leaf)) return 'Inspection answer';
  return field.split('.').map(titleCase).join(' › ');
}

function titleCase(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function shortId(id: string): string {
  if (!id) return '';
  return id.length > 12 ? id.slice(0, 8) : id;
}

/**
 * @process getAuditDocumentLabel
 *
 * Steps:
 * 1. Prefer name / displayName / entityName / caseNumber / parcelNumber / email.
 * 2. Fall back to a shortened document id.
 *
 * Inputs:
 * - document: before or after snapshot.
 * - fallbackId: documentId.
 *
 * Outputs:
 * - Human label for summaries.
 *
 * Failure Handling: never throws.
 * Idempotency: REQUIRED.
 */
export function getAuditDocumentLabel(
  document: Record<string, unknown> | null | undefined,
  fallbackId = '',
): string {
  if (!document) return shortId(fallbackId) || 'record';
  const candidates = [
    document.entityName,
    document.name,
    document.displayName,
    document.caseNumber,
    document.parcelNumber,
    document.email,
  ];
  for (const value of candidates) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return shortId(fallbackId) || 'record';
}

function collectionNoun(collection: string): string {
  return COLLECTION_NOUN[collection] ?? collection;
}

function caseTypeLabel(document: Record<string, unknown> | null | undefined): string {
  const caseType = document?.caseType;
  if (caseType && typeof caseType === 'object') {
    const typed = caseType as Record<string, unknown>;
    const name = typed.name ?? typed.label ?? typed.code;
    if (typeof name === 'string' && name.trim()) return name.trim();
  }
  if (typeof caseType === 'string' && caseType.trim()) return caseType.trim();
  return '';
}

/**
 * @process buildAuditSummary
 *
 * Steps:
 * 1. Resolve a human record label from the document.
 * 2. Phrase the action (Created / Updated / Completed / Added contact).
 *
 * Inputs:
 * - AuditSummaryInput.
 *
 * Outputs:
 * - End-user sentence for AuditLogEntry.summary.
 *
 * Failure Handling: never throws.
 * Idempotency: REQUIRED.
 */
export function buildAuditSummary(input: AuditSummaryInput): string {
  const { collection, action, extra } = input;
  const document = input.document ?? null;
  const label = extra?.entityName
    ?? getAuditDocumentLabel(document, String(document?._id ?? ''));
  const noun = collectionNoun(collection);
  const typeName = caseTypeLabel(document);
  const fy = typeof document?.fiscalYear === 'number' || typeof document?.fiscalYear === 'string'
    ? ` FY${document?.fiscalYear}`
    : '';
  const typeSuffix = typeName ? ` — ${typeName}${fy}` : fy ? ` —${fy}` : '';

  switch (action) {
    case 'create':
      if (collection === 'cases' || collection === 'inspectionCases') {
        return `Created inspection: ${label}${typeSuffix}`;
      }
      if (collection === 'contacts') {
        return `Created contact: ${label}`;
      }
      if (collection === 'entities' || collection === 'inspectableEntities') {
        return `Created location: ${label}`;
      }
      return `Created ${noun}: ${label}`;
    case 'delete':
      if (collection === 'cases' || collection === 'inspectionCases') {
        return `Deleted inspection: ${label}${typeSuffix}`;
      }
      if (collection === 'contacts') {
        return `Deleted contact: ${label}`;
      }
      return `Deleted ${noun}: ${label}`;
    case 'statusChange':
      if (collection === 'cases' || collection === 'inspectionCases') {
        const status = String(document?.caseStatus ?? document?.seriesStatus ?? '').trim();
        if (status === 'caseCompleted' || status === 'completed') {
          return `Completed inspection: ${label}${typeSuffix}`;
        }
        if (status === 'closedOut') {
          return `Closed out inspection: ${label}${typeSuffix}`;
        }
        if (status === 'archived' || document?._archived === true) {
          return `Archived inspection: ${label}${typeSuffix}`;
        }
        return `Updated inspection status: ${label}${typeSuffix}`;
      }
      return `Changed status of ${noun}: ${label}`;
    case 'addMember':
      return extra?.contactName
        ? `Added contact ${extra.contactName} to ${label}`
        : `Added contact to ${label}`;
    case 'removeMember':
      return extra?.contactName
        ? `Removed contact ${extra.contactName} from ${label}`
        : `Removed contact from ${label}`;
    case 'archive':
      return `Archived ${noun}: ${label}`;
    case 'restore':
      return `Restored ${noun}: ${label}`;
    default:
      if (collection === 'cases' || collection === 'inspectionCases') {
        return `Updated inspection: ${label}${typeSuffix}`;
      }
      if (collection === 'contacts') {
        return `Updated contact: ${label}`;
      }
      if (collection === 'entities' || collection === 'inspectableEntities') {
        return `Updated location: ${label}`;
      }
      return `Updated ${noun}: ${label}`;
  }
}
